import { COUNTRY_NAMES, BU_LABELS } from "../lib/types";
import type { Site, BusinessUnit, CountryISO2, SiteType } from "../lib/types";

export type Filters = {
  country: CountryISO2 | null;
  bu: BusinessUnit | null;
  site_type: SiteType | null;
};

export function FilterChips({
  sites,
  filters,
  onChange,
}: {
  sites: Site[];
  filters: Filters;
  onChange: (next: Filters) => void;
}) {
  const byCountry = countBy(sites, (s) => s.country);
  const byBu = countBy(sites, (s) => s.bu);
  const byType = countBy(sites, (s) => s.site_type);

  return (
    <div className="space-y-2.5">
      <FilterRow label="Country">
        {(Object.keys(byCountry) as CountryISO2[]).sort().map((c) => (
          <Chip
            key={c}
            label={`${c} ${byCountry[c]}`}
            title={COUNTRY_NAMES[c]}
            active={filters.country === c}
            onClick={() =>
              onChange({ ...filters, country: filters.country === c ? null : c })
            }
          />
        ))}
      </FilterRow>
      <FilterRow label="Business Unit">
        {(Object.keys(byBu) as BusinessUnit[]).sort().map((b) => (
          <Chip
            key={b}
            label={`${b} ${byBu[b]}`}
            title={BU_LABELS[b]}
            active={filters.bu === b}
            onClick={() => onChange({ ...filters, bu: filters.bu === b ? null : b })}
          />
        ))}
      </FilterRow>
      <FilterRow label="Site Type">
        {(Object.keys(byType) as SiteType[]).sort().map((t) => (
          <Chip
            key={t}
            label={`${t} ${byType[t]}`}
            active={filters.site_type === t}
            onClick={() =>
              onChange({ ...filters, site_type: filters.site_type === t ? null : t })
            }
          />
        ))}
      </FilterRow>
    </div>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 w-24 shrink-0">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  label,
  title,
  active,
  onClick,
}: {
  label: string;
  title?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-2 py-0.5 text-[11px] font-mono border rounded transition-colors ${
        active
          ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40"
          : "bg-zinc-800/40 text-zinc-300 border-zinc-800 hover:border-zinc-600 hover:text-zinc-100"
      }`}
    >
      {label}
    </button>
  );
}

function countBy<T, K extends string>(items: T[], fn: (item: T) => K): Record<K, number> {
  const out = {} as Record<K, number>;
  for (const it of items) {
    const k = fn(it);
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}
